import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { File2Service, NotificationService, UtilService, UtilityService } from '@shared/services';
import { FileDto } from 'swagger';
import { FileExtensionType } from '@shared/models/enums';
import { ConfirmationDialogComponent } from '@shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss', '../../../../src/assets/bootstrap-utilities.min.css'],
  standalone: false,
})
export class FileUploadComponent implements OnChanges {
  @Output() singleFileOutput: EventEmitter<FileDto | null> = new EventEmitter<FileDto | null>();
  @Output() multipleFilesOutput: EventEmitter<FileDto[] | []> = new EventEmitter<FileDto[] | []>();

  @Input() singleFileInput: FileDto | undefined | null;
  @Input() multipleFilesInput: FileDto[] | undefined | null;
  @Input() validFileExtensions: string = Object.keys(FileExtensionType)
    .map((key) => `.${key}`)
    .join(',');
  @Input() multiple = false;
  @Input() viewOnly = false;

  private utilService = inject(UtilService);
  private translate = inject(TranslateService);
  private notificationService = inject(NotificationService);
  private utilityService = inject(UtilityService);
  private file2Service = inject(File2Service);
  private dialog = inject(MatDialog);

  selectedFiles: FileDto[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['singleFileInput'] && this.singleFileInput) {
      this.selectedFiles = [this.singleFileInput];
    } else if (changes['singleFileInput'] && !this.singleFileInput) {
      this.selectedFiles = [];
    }

    if (changes['multipleFilesInput'] && this.multipleFilesInput) {
      this.selectedFiles = [...this.multipleFilesInput];
    } else if (changes['multipleFilesInput'] && !this.multipleFilesInput) {
      this.selectedFiles = [];
    }
  }

  public addFile(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file: File | undefined = input?.files?.[0];

    if (!file) {
      return;
    }

    const errorMessage = this.utilService.validateFileUpload(file, this.validFileExtensions);
    if (errorMessage !== null) {
      this.notificationService.showError(this.translate.instant(errorMessage), this.translate.instant('ERROR'));
      return;
    }

    this.utilService.convertFileToFileDto(file).subscribe({
      next: (fileDto) => {
        if (this.multiple) {
          this.selectedFiles.push(fileDto);
          this.multipleFilesOutput.emit(this.selectedFiles);
        } else {
          this.selectedFiles = [fileDto];
          this.singleFileOutput.emit(fileDto);
        }
      },
    });
  }

  public removeFile(index: number): void {
    const file = this.selectedFiles[index];

    if (file) {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: this.translate.instant('CONFIRMATION_DIALOG.ARE_YOU_SURE_DELETE_FILE', { fileName: file.fileName }),
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.selectedFiles.splice(index, 1);

          if (this.multiple) {
            this.multipleFilesOutput.emit(this.selectedFiles);
          } else {
            this.singleFileOutput.emit(null);
          }
        }
      });
    }
  }

  public downloadFile(event: MouseEvent, index: number): void {
    event.preventDefault();
    const file = this.selectedFiles[index];

    if (file?.id) {
      this.file2Service.downloadFile(file);
    } else {
      this.utilityService.downloadFile(file);
    }
  }
}
